// Console errors are corrected -30-07-24 - Dev Srini
import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useTheme } from '@mui/material/styles';
import { select } from 'd3-selection';
import { useMediaQuery } from '@mui/system';

const ArcsComponent = ({ circleData }) => {
  const theme = useTheme();
  const svgRef = useRef(null);
  const range = useMediaQuery(theme.breakpoints.between(1300, 1430));
  const range1 = useMediaQuery(theme.breakpoints.between(880, 1160));
  const range2 = useMediaQuery(theme.breakpoints.between(0, 880));

  const data = circleData?.data ?? [];

  const maxValueArray = data.map((ele) => Math.abs(ele.value));
  const maxValue = maxValueArray.length > 0 ? Math.max(...maxValueArray) : 0;

  const mapValueToAngle = (value, maxValue) => {
    if (maxValue === 0) return 180; // Return a default angle if maxValue is zero
    const maxAngle = 450;
    const startAngle = 180;
    const normalizedValue = (value / maxValue) * (maxAngle - startAngle);
    return startAngle + normalizedValue;
  };

  const data1 = data.map((ele) => {
    if (ele.name === "NetAssets") {
      return { ...ele, name: "Invested", value: ele.value, angle: mapValueToAngle(Math.abs(ele.value), maxValue), color: "#517AAF" };
    }
    if (ele.name === "MarketValue") {
      return { ...ele, name: "Current", value: ele.value, angle: mapValueToAngle(Math.abs(ele.value), maxValue), color: ele.value < 0 ? "red" : "#FF7F09" };
    }
    if (ele.name === "Profit Notional") {
      return { ...ele, value: ele.value, angle: mapValueToAngle(Math.abs(ele.value), maxValue), color: ele.value < 0 ? "red" : "#7DAB1C" };
    }
    return ele;
  });

  useEffect(() => {
    const svg = select(svgRef.current);
    svg.selectAll('*').remove();
    const width = 250;
    const height = 250;
    const maxRadius = Math.min(width, height) / 5;
    const gapAngle = 3; // Set the desired gap angle

    svg
      .append('text')
      .attr('x', 10) // Set the x position from the left
      .attr('y', 10) // Set the y position with a gap of 20 between each line
      .attr('dy', '0.6em') // Adjust vertical alignment
      .attr('fill', '#F3D45E')
      .style('font-size', '12px') // Set font size for value
      .style('font-family', 'Cardium B') // Set font family
      .style('font-style', 'normal')
      .style('font-weight', '200')
      .style('line-height', 'normal')
      .text(`${circleData?.title ?? 'Net Assets'}`);

    svg.append("line")
      .attr("x1", 0)
      .attr("y1", 30)
      .attr("x2", range2 ? 1500 : 500)
      .attr("y2", 30)
      .attr("stroke", "black")
      .attr("stroke-width", 1.5);

    data1?.forEach((data, index) => {
      console.log("data", data);
      const innerRadius = maxRadius - index * 12 + gapAngle;
      const outerRadius = maxRadius - (index - 1) * 12; // Adjust the factor for decreasing outer radius

      //#region Create background arc with gray color
      const backgroundArc = d3
        .arc()
        .innerRadius(innerRadius)
        .outerRadius(outerRadius)
        .cornerRadius(8);

      const backgroundArcData = {
        startAngle: 3.14,
        endAngle: 7.8
      };

      console.log(backgroundArcData);

      svg
        .append('path')
        .datum(backgroundArcData)
        .attr('d', backgroundArc)
        .attr('fill', '#D9D9D933')
        .attr('transform', `translate(${range || range1 ? (width / 1.5) : (width / 1.2)},${height / 2})`);
      const arc = d3
        .arc()
        .innerRadius(innerRadius)
        .outerRadius(outerRadius)
        .cornerRadius(8);

      //#endregion Create background arc with gray color  
      const arcData = {
        startAngle: 3.14,
        endAngle: data.angle * (Math.PI / 180)
      };

      //#region Arc Text
      const textAngle = 80 % 360; // Angle for positioning text
      const textX = (outerRadius + innerRadius) / 2 * Math.cos((textAngle / 180) * Math.PI);
      const textY = (outerRadius + innerRadius) / 2 * Math.sin((textAngle / 180) * Math.PI);

      svg
        .append('text')
        .attr('x', range || range1 ? (textX + (width / 1.5)) : (textX + (width / 1.2)))
        .attr('y', textY + height / 1.95)
        .attr('text-anchor', 'left')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '10px')
        .style('font-family', 'Cardium B')
        .style('fill', '#999')
        .style('font-style', 'normal')
        .style('font-weight', '200')
        .style('line-height', 'normal')
        .text(`${data.name}`);
      //#endregion 

      //#region Main Text
      svg
        .append('text')
        .attr('x', 10) // Set the x position from the left
        .attr('y', (index * 55) + 60) // Set the y position with a gap of 20 between each line
        .attr('dy', '0em') // Adjust vertical alignment
        .attr('fill', data.color)
        .style('font-size', range || range1 ? "12px" : '16px') // Set font size for value
        .style('font-family', 'Cardium B') // Set font family
        .text(`${parseFloat(data.value).toFixed(2)}`);

      svg
        .append('text')
        .attr('x', 10) //
        .attr('y', (index * 55) + 75) // Set the y position below the name text
        .attr('dy', '0em') // Adjust vertical alignment
        .attr('fill', '#999')
        .style('font-size', '12px') // Set font size
        .style('font-family', 'Cardium B')
        .style('font-style', 'normal') // Set font style
        .style('font-weight', '400') // Set font weight
        .style('line-height', '1.9') // Set line height
        .text(data.name);
      //#endregion
      svg
        .append('path')
        .datum(arcData)
        .attr('d', arc)
        .attr('fill', data.color)
        .attr('transform', `translate(${range || range1 ? (width / 1.5) : (width / 1.2)},${height / 2})`);
    });

  }, [circleData, range, range1, range2]);

  return <svg width="100%" height={200} style={{ background: "#241E41" }} ref={svgRef}></svg>;
};

export default ArcsComponent;
